'use client';

import React, { forwardRef, useId, useRef } from 'react';
import {
  CalendarDays,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Eraser,
  Hash,
  Link2 as LinkIcon,
  Lock,
  Mail,
  Phone,
  Search,
  Type,
} from 'lucide-react';
import './Input.css';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  floatingLabel?: string;
  /** Estilo do floating label (so tem efeito junto com `floatingLabel`):
   *  - `inside`: label interna que sobe no canto superior do campo (Material classico).
   *  - `outline`: label sobe para a LINHA da borda com box ao redor (notch com caixa).
   *  - `above`: label sobe para FORA/ACIMA da borda superior, sem box e sem fundo.
   *  Quando ausente, o estilo e resolvido por `outlineLabel` (alias) ou cai em `inside`. */
  labelFloat?: 'inside' | 'outline' | 'above';
  /**
   * @deprecated Use `labelFloat='outline'`. Alias mantido para nao quebrar usos
   * existentes (modais CRUD, telas de barbeiro). So tem efeito com `floatingLabel`
   * e quando `labelFloat` nao for passado.
   */
  outlineLabel?: boolean;
  error?: string;
  helperText?: string;
  hint?: string;
  prefixIcon?: React.ReactNode;
  icon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  /** Botao de acao acoplado dentro do input (a direita). Ex.: adicionar tag.
   *  Quando ausente, e um input normal. */
  action?: {
    icon: React.ReactNode;
    onClick: () => void;
    label: string;
    disabled?: boolean;
    primary?: boolean;
  };
  containerClassName?: string;
  wrapperClassName?: string;
  showErrorMessage?: boolean;
  /** Mostra um botao de limpar (a direita) quando o campo tem valor. */
  clearable?: boolean;
  /** Disparado ao clicar no limpar (o consumidor zera o valor controlado). */
  onClear?: () => void;
}

function resolveDefaultInputIcon(
  type: React.HTMLInputTypeAttribute | undefined,
  name: string | undefined,
  id: string,
): React.ReactNode {
  const key = `${type ?? ''} ${name ?? ''} ${id}`.toLowerCase();

  if (type === 'hidden' || type === 'checkbox' || type === 'radio' || type === 'file') return null;
  if (type === 'email' || key.includes('email')) return <Mail className="input__semantic-icon" />;
  if (type === 'tel' || key.includes('phone') || key.includes('celular') || key.includes('telefone')) return <Phone className="input__semantic-icon" />;
  if (type === 'password' || key.includes('password') || key.includes('senha')) return <Lock className="input__semantic-icon" />;
  if (type === 'date' || type === 'datetime-local' || type === 'month' || key.includes('date') || key.includes('data')) return <CalendarDays className="input__semantic-icon" />;
  if (type === 'search' || key.includes('search') || key.includes('busca')) return <Search className="input__semantic-icon" />;
  if (key.includes('price') || key.includes('valor') || key.includes('revenue') || key.includes('amount')) return <DollarSign className="input__semantic-icon" />;
  if (type === 'number' || key.includes('duration') || key.includes('qty') || key.includes('quantity')) return <Hash className="input__semantic-icon" />;
  if (type === 'url' || key.includes('url') || key.includes('link')) return <LinkIcon className="input__semantic-icon" />;

  return <Type className="input__semantic-icon" />;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      wrapperClassName,
      label,
      floatingLabel,
      labelFloat,
      outlineLabel,
      error,
      helperText,
      hint,
      prefixIcon,
      icon,
      suffixIcon,
      trailingIcon,
      action,
      id,
      placeholder,
      required,
      showErrorMessage = false,
      clearable = false,
      onClear,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? `input-${generatedId.replace(/:/g, '')}`;

    // Stepper proprio para type=number (substitui o spinner nativo). Precisa de
    // um ref interno para chamar stepUp/stepDown e notificar React/RHF.
    const innerRef = useRef<HTMLInputElement | null>(null);
    const setRefs = (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    };
    const showStepper =
      props.type === 'number' &&
      !props.disabled &&
      !props.readOnly &&
      !action &&
      !suffixIcon &&
      !trailingIcon;
    const stepValue = (direction: 1 | -1) => {
      const el = innerRef.current;
      if (!el) return;
      if (direction > 0) el.stepUp();
      else el.stepDown();
      // Dispara o evento nativo de input para o React/react-hook-form captarem.
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.focus();
    };
    const defaultIcon = resolveDefaultInputIcon(props.type, props.name, inputId);
    const leadingIcon = prefixIcon !== undefined ? prefixIcon : icon !== undefined ? icon : defaultIcon;
    const trailing = suffixIcon ?? trailingIcon;
    const hasValue = props.value != null && String(props.value).length > 0;
    const showClear = clearable && hasValue && !props.disabled && !props.readOnly;
    const message = error ?? helperText ?? hint;
    const messageId = message ? `${inputId}-desc` : undefined;
    const isFloating = Boolean(floatingLabel);
    const resolvedPlaceholder = isFloating ? (placeholder ?? ' ') : placeholder;
    const resolvedFloat: 'inside' | 'outline' | 'above' =
      labelFloat ?? (outlineLabel ? 'outline' : 'inside');
    const FLOAT_CLASS: Record<typeof resolvedFloat, string> = {
      inside: ' input--floating',
      outline: ' input--float-outline',
      above: ' input--float-outline input--float-above',
    };
    const floatClass = isFloating ? FLOAT_CLASS[resolvedFloat] : '';

    return (
      <div className={`${containerClassName ?? wrapperClassName ?? ''}`}>
        {label && !isFloating && (
          <label className="field__label" htmlFor={inputId}>
            {label}
            {required && <span className="req" aria-hidden="true">*</span>}
          </label>
        )}
        <div className={`input${leadingIcon ? ' input--icon' : ''}${floatClass}${trailing || action || showClear ? ' input--with-action' : ''}${showStepper ? ' input--with-stepper' : ''}${error ? ' input--error' : ''}`}>
          {leadingIcon && <span className="input__icon">{leadingIcon}</span>}
          <input
            ref={setRefs}
            id={inputId}
            className={className}
            placeholder={resolvedPlaceholder}
            required={required}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={messageId}
            {...props}
            /* Por padrao os formularios de CRUD NAO querem autofill/historico do
               navegador (entra lixo de cadastros anteriores nos campos principais).
               Default "off"; quem quer autofill (login/registro/perfil/booking)
               passa um autoComplete explicito (email/tel/new-password/...). */
            autoComplete={props.autoComplete ?? 'off'}
          />
          {floatingLabel && (
            <span className="input__floating-label">
              {floatingLabel}
              {required && <span className="req" aria-hidden="true">*</span>}
            </span>
          )}
          {showClear ? (
            <button
              type="button"
              className="input__action input__clear"
              onClick={onClear}
              aria-label="Limpar"
              tabIndex={-1}
            >
              <Eraser aria-hidden="true" />
            </button>
          ) : action ? (
            <button
              type="button"
              className={`input__action${action.primary ? ' input__action--primary' : ''}`}
              onClick={action.onClick}
              disabled={action.disabled}
              aria-label={action.label}
            >
              {action.icon}
            </button>
          ) : trailing ? (
            <span className="input__action">{trailing}</span>
          ) : null}
          {showStepper && (
            <span className="input__stepper">
              <button
                type="button"
                tabIndex={-1}
                className="input__stepper-btn"
                onClick={() => stepValue(1)}
                aria-label="Aumentar"
              >
                <ChevronUp aria-hidden="true" />
              </button>
              <button
                type="button"
                tabIndex={-1}
                className="input__stepper-btn"
                onClick={() => stepValue(-1)}
                aria-label="Diminuir"
              >
                <ChevronDown aria-hidden="true" />
              </button>
            </span>
          )}
        </div>
        {showErrorMessage && error && (
          <div id={messageId} className="field__error" role="alert">
            {error}
          </div>
        )}
        {/* helper/hint rendered here when no error and no FormField parent.
            Error messages can be enabled with showErrorMessage for compact
            DS forms without an outer FormField. */}
        {(helperText || hint) && !error && (
          <div id={messageId} className="field__hint">
            {helperText ?? hint}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
